import { useEffect, useRef, useState } from "react";
import { ChevronUp } from "lucide-react";
import galegoFlag from "@/assets/language-galego.png";
import spanishFlag from "@/assets/language-espanol.png";
import englishFlag from "@/assets/language-english.png";
import { UI_TRANSLATIONS, type KioskLanguage } from "@/lib/i18n";

export type { KioskLanguage } from "@/lib/i18n";

interface LanguageSelectorProps {
  value: KioskLanguage;
  onChange: (language: KioskLanguage) => void;
}

const LANGUAGES: Array<{
  code: KioskLanguage;
  label: string;
  flag: string;
}> = [
  { code: "gl", label: "Galego", flag: galegoFlag },
  { code: "es", label: "Español", flag: spanishFlag },
  { code: "en", label: "English", flag: englishFlag },
];

export const LanguageSelector = ({ value, onChange }: LanguageSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const selectedLanguage = LANGUAGES.find(({ code }) => code === value) ?? LANGUAGES[0];
  const translations = UI_TRANSLATIONS[value];

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const selectLanguage = (language: KioskLanguage) => {
    onChange(language);
    setIsOpen(false);
  };

  return (
    <div ref={selectorRef} className="language-selector">
      {isOpen && (
        <div
          className="language-selector-menu"
          role="listbox"
          aria-label={translations.languageMenu}
        >
          {LANGUAGES.filter(({ code }) => code !== value).map((language) => (
            <button
              key={language.code}
              type="button"
              className="language-menu-option"
              role="option"
              aria-selected="false"
              onClick={() => selectLanguage(language.code)}
            >
              <img src={language.flag} alt="" className="language-flag" />
              <span>{language.label}</span>
            </button>
          ))}
        </div>
      )}

      <button
        type="button"
        className="language-current"
        aria-label={`${translations.currentLanguage}: ${selectedLanguage.label}. ${translations.changeLanguage}`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        <img src={selectedLanguage.flag} alt="" className="language-flag" />
        <span className="language-current-label">{selectedLanguage.label}</span>
        <ChevronUp
          aria-hidden="true"
          className={`language-chevron ${isOpen ? "is-open" : ""}`}
        />
      </button>
    </div>
  );
};
